import React, { useEffect, useState } from "react";
import { observer } from "mobx-react";
import { useStores, useTracking } from "netbank-shared/src/hooks";
import rootStyles from "../Root.scss";
import { IOffersPage } from "netbank-shared/src/libs/models/Content/Page";
import { tx } from "netbank-shared/src/libs/i18n";
import styles from "../Page.scss";
import offerStyles from "./Offers.scss";
import { ContentArea } from "~views/shared/ContentArea/ContentArea";
import { OfferEmptyState } from "./OfferEmptyState";
import { CmsSkeleton } from "../Skeletons/CmsSkeleton";
import { useNavigate, useSearchParams } from "react-router-dom";
import { OfferPageButtonSection } from "./OfferPageButtonSection";
import { OfferCategory } from "netbank-shared/src/libs/models/Offer/Offer";

export const OffersPage = observer(() => {
  const { contentStore, offerStore } = useStores();
  const dataLayer = useTracking((window as any).dataLayer);
  const page = contentStore.currentPage as IOffersPage;
  const [isConsuming, setIsConsuming] = useState(false);
  const [consumeOfferResponse, setConsumeOfferResponse] = useState<boolean | undefined>(undefined);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const offerId = searchParams.get("offerId") || "";

  const trackingCategory = offerStore.getTrackingCategory();

  const sectionClasses = [rootStyles.noPaddingOnInternalSections];
  const wrapperClasses = [];

  if (page.wrapInWhiteCard) {
    wrapperClasses.push(offerStyles.whiteBoxContent);
  }

  if (page.disableDividersBetweenBlocks) {
    sectionClasses.push(rootStyles.noDividerOnInternalSections);
  }

  const accountPath = `${tx("routing.lang")}/${tx(`routing.${offerStore.getCurrentAccountTypeRoute()}`)}`;
  const accountNumber = `${offerStore.currentAccount?.accountNumber.replace(/^0+/, "")}`;
  const returnPath = `/${accountPath}/${accountNumber}`;

  const returnToAccount = () => navigate(returnPath);
  useEffect(() => {
    const fetchOffer = async () => {
      if (!offerStore.currentOffer || offerStore.currentOffer.offerId !== offerId) {
        await offerStore.setCurrentOfferById(offerId);
      }
    };
    fetchOffer();

    return () => {
      offerStore.setCurrentOffer(undefined);
    };
  }, [offerId]);

  const consumeOffer = async () => {
    setIsConsuming(true);
    const isItal = offerStore.currentOffer?.offerCategory === OfferCategory.ITAL;
    const response = isItal
      ? await offerStore.handleOfferAction(OfferCategory.ITAL)
      : await offerStore.consumeOffer(offerId);

    window.scrollTo(0, 0);
    if (response?.ok) {
      const trackingAction = offerStore.getTrackingActionSuccess();
      const trackingEvent = offerStore.getTrackingEventPrefix();
      const trackingValue = offerStore.getTrackingValue();
      if (trackingCategory) {
        dataLayer.pushInteraction(trackingCategory, trackingAction, trackingEvent, trackingValue);
      }
      setConsumeOfferResponse(true);
    } else {
      const trackingAction = offerStore.getTrackingActionError();
      if (trackingCategory) {
        dataLayer.pushInteraction(trackingCategory, trackingAction);
      }
      setConsumeOfferResponse(false);
    }
    setIsConsuming(false);

    if (isItal) {
      returnToAccount();
    }
  };

  const isValidOffer = offerStore.validateCurrentOffer();

  if (offerStore.isLoadingOffer) {
    return <CmsSkeleton />;
  }

  if (!isValidOffer) {
    return (
      <div className={styles.wrapper}>
        <section>
          <OfferEmptyState data={page.errorCaseEmptyState} />
        </section>
      </div>
    );
  }

  // TODO PPI
  return (
    <div className={styles.wrapper}>
      {typeof consumeOfferResponse === "undefined" && (
        <>
          <section className={sectionClasses.join(" ")}>
            <div className={wrapperClasses.join(" ")}>
              <ContentArea />
              <OfferPageButtonSection
                cancelOffer={returnToAccount}
                consumeText={page.consumeOfferButtonText}
                cancelButtonText={page.cancelButtonText}
                termsConditionText={page.termsConditionText}
                isConsuming={isConsuming}
                checkBoxNotRequired={page.checkBoxNotRequired}
                consumeOffer={consumeOffer}
              />
            </div>
          </section>
        </>
      )}
      {consumeOfferResponse === true && (
        <section>
          <OfferEmptyState accountPath={returnPath} data={page.happyCaseEmptyState} />
        </section>
      )}
      {consumeOfferResponse === false && (
        <section>
          <OfferEmptyState data={page.errorCaseEmptyState} />
        </section>
      )}
    </div>
  );
});
